import { useEffect, useRef } from "react";

declare global {
  interface Window {
    grecaptcha: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          size: string;
          callback: (token: string) => void;
        }
      ) => number;
      execute: (widgetId: number) => void;
      reset: (widgetId: number) => void;
    };
  }
}

type UseInvisibleRecaptchaOptions = {
  siteKey: string;
  onToken: (token: string) => void;
  executeOnMount?: boolean;
  /** When false, captcha init is skipped (e.g. closed modals) */
  enabled?: boolean;
};

export function useInvisibleRecaptcha({
  siteKey,
  onToken,
  executeOnMount = false,
  enabled = true,
}: UseInvisibleRecaptchaOptions) {
  const captchaRef = useRef<HTMLDivElement>(null);
  const captchaWidgetIdRef = useRef<number | null>(null);
  const initialExecutedRef = useRef(false);
  const onTokenRef = useRef(onToken);

  onTokenRef.current = onToken;

  const resetCaptcha = () => {
    if (captchaWidgetIdRef.current !== null && window.grecaptcha) {
      try {
        window.grecaptcha.reset(captchaWidgetIdRef.current);
      } catch (error) {
        console.error("Error resetting captcha:", error);
      }
    }
  };

  const renderCaptcha = () => {
    if (!captchaRef.current || captchaWidgetIdRef.current !== null) return;

    try {
      if (window.grecaptcha?.render) {
        captchaWidgetIdRef.current = window.grecaptcha.render(
          captchaRef.current,
          {
            sitekey: siteKey,
            size: "invisible",
            callback: (token: string) => onTokenRef.current(token),
          }
        );

        if (executeOnMount && !initialExecutedRef.current) {
          initialExecutedRef.current = true;
          setTimeout(() => executeCaptcha(), 500);
        }
      }
    } catch (error) {
      console.error("Error rendering captcha:", error);
    }
  };

  const executeCaptcha = () => {
    if (!window.grecaptcha) {
      console.error("grecaptcha not loaded yet");
      return;
    }

    if (captchaWidgetIdRef.current === null) {
      renderCaptcha();
      setTimeout(() => {
        if (captchaWidgetIdRef.current !== null) {
          window.grecaptcha.execute(captchaWidgetIdRef.current);
        }
      }, 1000);
      return;
    }

    try {
      window.grecaptcha.execute(captchaWidgetIdRef.current);
    } catch (error) {
      console.error("Error executing captcha:", error);
    }
  };

  useEffect(() => {
    if (!enabled) return;

    const initCaptcha = () => {
      if (window.grecaptcha) {
        renderCaptcha();
      } else {
        setTimeout(initCaptcha, 100);
      }
    };

    initCaptcha();

    return () => resetCaptcha();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return { captchaRef, executeCaptcha, resetCaptcha };
}
